import { useLocation } from '@reach/router'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useState } from 'react'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'

import { useUserRegion } from '@/hooks'
import Button from '@/modules/components/Button'
import Container from '@/modules/components/Container'
import Flex from '@/modules/components/Flex'
import { useIsPortableDevice } from '@/modules/components/hooks/useIsPortableDevice'
import Icon from '@/modules/components/Icon'
import { regionCookieName, Regions } from '@/modules/configs/region'
import { NavBarModal } from '@/modules/NavBar/components/NavBarPopup'
import { useSpanishAccess } from '@/modules/useSpanishAccess'
import { getRegionPath } from '@/modules/utils'

import { DownloadButtons } from './components/DownloadButtons'
import { Logo } from './components/Logo'
import { FirstTimePopup } from './components/NavBarPopup/FirstTimePopup'
import {
  CompanyLink,
  CompanySec,
  Defi,
  Eco,
  Exchange,
  Learn,
  LearnSec,
  Products,
  Section,
} from './data'
import { DesktopMenu } from './desktop'
import { SearchBar } from './desktop/SearchBar'
import { MobileMenu } from './mobile'
import { userRegionIndicator } from './useRegionIndicator'
import { useToggleMenu } from './useToggleMenu'
import { extendsExpireYears, getLanguages, getRegions } from './utils'

type NavbarProps = {
  variant?: 'dark' | 'light'
  layerIndex?: number
  products: Products[]
  isExchange: boolean
  exchange: Exchange[]
  isDefi: boolean
  defi: Defi[]
  isEco: boolean
  eco: Eco[]
  company: CompanyLink[]
  learn_desktop?: LearnSec[]
  company_desktop: CompanySec[]
  products_desktop: Section[]
  isShowNFT: boolean
  isShowPrice: boolean
  isShowLanguages: boolean
  logoHref: string
  languages: string[]
  learn?: Learn[]
  isShowGeoIcon: boolean
}

export const Navbar = ({
  variant = 'dark',
  layerIndex,
  products,
  isExchange,
  exchange,
  isDefi,
  defi,
  isEco,
  eco,
  company,
  learn_desktop,
  company_desktop,
  products_desktop,
  isShowNFT,
  isShowPrice,
  logoHref,
  languages,
  learn,
  isShowGeoIcon,
}: NavbarProps) => {
  const isMobileOrTablet = useIsPortableDevice()
  const { i18n } = useTranslation('navigation')
  const { language: currentLanguage } = i18n
  const [isMobileOpen, toggleMobile] = useToggleMenu()
  const [isPopupOpen, setPopupOpen] = useState(false)
  const [, setCookie] = useCookies([regionCookieName, 'locale'])
  const [isRegionPop, setRegionPop] = useState(false)
  const userRegion = useUserRegion()
  const location = useLocation()
  const regionPath = getRegionPath(location.pathname)

  const isSP = useSpanishAccess()

  const [suggestRegion, allRegion] = getRegions(userRegion, regionPath, {
    isSpanishAccess: isSP,
  })

  const [suggestLangs, allLangs] = getLanguages(currentLanguage, languages)

  const regionIndicatorSrc = userRegionIndicator(
    regionPath.toUpperCase() as Regions
  )

  const onPopupClose = () => {
    setPopupOpen(false)
  }

  const onRegionClose = () => {
    setRegionPop(false)
  }

  return (
    <>
      {/* TODO styles need refactor */}
      <Flex
        position="sticky"
        top={0}
        left={0}
        width="100%"
        zIndex={layerIndex ?? 99}
        alignItems="center"
        backgroundColor="backgroundDark"
        height={'62px'}
        customCss={{ height: ['62px', '62px', '78px'] }}
        maxWidth={'100vw'}
        // TODO temp comment out the collapse height logic, the hook is in `useScrollCollapse.ts`
        // height={isCollapsed ? "navBarHeightCollapsed" : "navBarHeight"}
        // customCss={{ transition: "height 0.2s" }}
      >
        <Container flex={1}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            customCss={{ px: ['8px', '8px', '0px'] }}
          >
            <Logo variant={variant} href={logoHref} />
            <Flex alignItems="center">
              <DesktopMenu
                alignItems="center"
                marginRight={4}
                display={['none', 'none', 'flex']}
                variant={variant}
                company={company_desktop}
                products={products_desktop}
                isShowNFT={isShowNFT}
                isShowPrice={isShowPrice}
                learnSec={learn_desktop}
              />
              <SearchBar />
              <DownloadButtons />
              <Button
                variant="plain"
                marginLeft={[3, 5]}
                mr={'3px'}
                display={['block', 'block', 'none']}
                onClick={toggleMobile}
              >
                <Icon.Menu fontSize={22} color="secondaryLightGrey" />
              </Button>
            </Flex>
            <NavBarModal
              isOpen={isPopupOpen}
              setOpen={onPopupClose}
              suggestRegion={suggestRegion}
              allRegion={allRegion}
              suggestLangs={suggestLangs}
              allLangs={allLangs}
              onSetRegion={(region) => {
                setCookie(regionCookieName, region, {
                  expires: extendsExpireYears(99),
                  path: '/',
                })
                setPopupOpen(false)
              }}
            />
            <FirstTimePopup
              isMobile={isMobileOrTablet}
              isOpen={isRegionPop}
              setOpen={onRegionClose}
              allRegion={allRegion}
              onClick={(region) => {
                setCookie(regionCookieName, region, {
                  expires: extendsExpireYears(99),
                  path: '/',
                })
                setRegionPop(false)
              }}
            />
          </Flex>
        </Container>
      </Flex>
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 99,
            }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ bounce: 0, duration: 0.2 }}
          >
            <MobileMenu
              onCloseButtonClick={toggleMobile}
              onSubMenuItemClick={toggleMobile}
              products={products}
              isExchange={isExchange}
              exchange={exchange}
              isDefi={isDefi}
              defi={defi}
              isEco={isEco}
              eco={eco}
              company={company}
              isShowNFT={isShowNFT}
              isShowPrice={isShowPrice}
              languages={languages}
              learn={learn}
              allRegion={allRegion}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
